import React, { useEffect, useMemo, useState } from 'react';

import Link from '@amzn/meridian/link';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { ASSOCIATE_PROFILE_BASEURL, PLACEMENT_DETAILS_BASEURL } from 'src/constants/Routes';
import { type AllocatedJobShift } from 'src/services/placementOverview/types/allocatedJobShift';
import { type TableDataRow } from 'src/services/placementOverview/types/tableDataRow';
import { setCurrentPage, setTotalPages } from 'src/store/placementOverviewPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { SortStrategy } from 'src/utils/sortStrategies';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

type TableHeader = {
  sortColumn: keyof TableDataRow;
  label: string;
  condition?: boolean;
  id?: string;
};

const getDefaultTableHeaders = (
  daysInCategoryTableHeader: string,
  canShow180DayCounter: boolean,
  isClosedPlacementsTab: boolean,
): TableHeader[] => [
  { sortColumn: 'associateNameOrAlias', label: 'Associate Name' },
  { sortColumn: 'caseId', label: 'Case Id' },
  { sortColumn: 'jobs', label: 'Job' },
  { sortColumn: 'placementStartDate', label: 'Start Date' },
  { sortColumn: 'placementEndDate', label: 'End Date' },
  { sortColumn: 'daysInCategory', label: `${daysInCategoryTableHeader} (days)` },
  {
    sortColumn: 'totalAccommodatedDaysByAssociate',
    label: 'Total Days in Placement(s)',
    condition: canShow180DayCounter,
    id: 'placementOverview__table__totalDaysInPlacement',
  },
  {
    sortColumn: 'closedAtDate',
    label: 'Closed At Date',
    condition: isClosedPlacementsTab,
  },
  { sortColumn: 'status', label: 'Status' },
];

const getTransferredInTableHeaders = (): TableHeader[] => [
  { sortColumn: 'associateNameOrAlias', label: 'Associate Name' },
  { sortColumn: 'caseId', label: 'Case Id' },
  { sortColumn: 'previousSite', label: 'Transferred From Site' },
  { sortColumn: 'transferredAtDate', label: 'Placement Transfer Date' },
  {
    sortColumn: 'daysInCategory',
    label: 'Time in Transferred Status (days)',
  },
  { sortColumn: 'status', label: 'Status' },
];

// choose sort strategy based on column type.
// default is sort by string
const evaluateSortStrategy = (sortColumn: string) => {
  const columnStrategies = {
    placementStartDate: SortStrategy.DATE,
    placementEndDate: SortStrategy.DATE,
    closedAtDate: SortStrategy.DATE,
    transferredAtDate: SortStrategy.DATE,
    daysInCategory: SortStrategy.NUMBER,
    totalAccommodatedDaysByAssociate: SortStrategy.NUMBER,
  };

  return columnStrategies[sortColumn as keyof typeof columnStrategies] || SortStrategy.DEFAULT;
};

const PlacementsOverviewTable = ({ currentTab }: { currentTab: string }) => {
  const dispatch = useAppDispatch();
  const {
    activePlacementsTableData,
    pendingPlacementsTableData,
    expiredPlacementsTableData,
    closedPlacementsTableData,
    transferredInPlacementsTableData,
    searchField,
    currentPage,
    perPage,
  } = useAppSelector((state) => state.placementOverviewPage);
  const { selectedSite } = useAppSelector((state) => state.sites);
  const { featureFlags } = useAppSelector((state) => state.user);

  const [tableData, setTableData] = useState<TableDataRow[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof TableDataRow>('associateNameOrAlias');
  const [sortStrategy, setSortStrategy] = useState(SortStrategy.DEFAULT);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const [daysInCategoryTableHeader, setDaysInCategoryTableHeader] = useState('');

  const onSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: keyof TableDataRow;
    sortDirection: TableSortDirection;
  }) => {
    const sortStrategy = evaluateSortStrategy(sortColumn);
    setSortStrategy(sortStrategy);
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  // show 180 day counter if not in closed placements and not in a canada site
  const canShow180DayCounter = useMemo(() => {
    return currentTab !== 'closedPlacements' && selectedSite.countryId !== COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID;
  }, [currentTab, selectedSite.countryId]);

  useEffect(() => {
    switch (currentTab) {
      case 'pendingPlacements':
        setTableData(pendingPlacementsTableData);
        setDaysInCategoryTableHeader('Dwell Time');
        break;
      case 'expiredPlacements':
        setTableData(expiredPlacementsTableData);
        setDaysInCategoryTableHeader('Time Expired');
        break;
      case 'activePlacements':
        setTableData(activePlacementsTableData);
        setDaysInCategoryTableHeader('Time in Placement');
        break;
      case 'closedPlacements':
        setTableData(closedPlacementsTableData);
        setDaysInCategoryTableHeader('Time Closed');
        break;
      case 'transferredInPlacements':
        setTableData(transferredInPlacementsTableData);
        break;
    }
  }, [
    currentTab,
    activePlacementsTableData,
    pendingPlacementsTableData,
    expiredPlacementsTableData,
    closedPlacementsTableData,
    transferredInPlacementsTableData,
  ]);

  const { paginatedData, totalPages } = useMemo(() => {
    const jobsNotDisplayed = (row: any) => !row.jobsDisplayed;
    return filterSortAndPaginateTableData(
      tableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
      sortStrategy,
      [
        ['jobs', jobsNotDisplayed],
        ['jobTypes', jobsNotDisplayed],
        ['shiftCodes', jobsNotDisplayed],
      ],
    );
  }, [sortColumn, sortDirection, tableData, searchField, currentPage, perPage]);

  useEffect(() => {
    dispatch(setTotalPages(totalPages));
  }, [totalPages]);

  useEffect(() => {
    // go back to first page when filter/sort
    dispatch(setCurrentPage(1));
  }, [searchField, sortColumn, sortDirection, perPage, dispatch]);

  const tableHeaders =
    currentTab !== 'transferredInPlacements'
      ? getDefaultTableHeaders(daysInCategoryTableHeader, canShow180DayCounter, currentTab === 'closedPlacements')
      : getTransferredInTableHeaders();

  const handleEmployeeCell = (associateFullName: string | null, alias: string, shiftCode: string) => {
    const hasAssociateFullName = associateFullName !== null;
    return (
      <>
        {hasAssociateFullName &&
          (featureFlags.PERSONA ? (
            <Link href={`${ASSOCIATE_PROFILE_BASEURL}/${alias}`} type="secondary">
              {associateFullName}
            </Link>
          ) : (
            <Text type="b300">{associateFullName}</Text>
          ))}
        <Text type={hasAssociateFullName ? 'b200' : 'b300'} color={hasAssociateFullName ? 'secondary' : 'primary'}>
          {alias}@
        </Text>
        <Text type="b200" color="secondary">
          {shiftCode ?? ''}
        </Text>
      </>
    );
  };

  const handleJobs = (
    jobs: (string | null)[],
    jobTypes: (string | null)[],
    shiftCodes: (string | null)[],
    jobSuffix: string,
  ) =>
    jobs.length <= 1 ? (
      <>
        <Text type="b300">
          {jobs[0]} {jobSuffix}
        </Text>
        <Text type="b200" color="secondary">
          {jobTypes[0] ?? '-'}
        </Text>
        <Text type="b200" color="secondary">
          {shiftCodes[0] ?? '-'}
        </Text>
      </>
    ) : (
      `${jobs.length} jobs`
    );

  const visibleHeaderCount = tableHeaders.filter((h) => h.condition ?? true).length;

  const handleAllocatedJobShifts = (
    allocatedJobShifts: AllocatedJobShift[],
    jobs: (string | null)[],
    jobTypes: (string | null)[],
    shiftCodes: (string | null)[],
    jobSuffix: string,
  ) =>
    allocatedJobShifts.length > 1 &&
    allocatedJobShifts.map((_: AllocatedJobShift, index: number) => (
      <TableRow key={index} backgroundColor="secondary">
        <TableCell />
        <TableCell />
        <TableCell>
          <Text type="b300">
            {jobs[index]} {jobSuffix}
          </Text>
          <Text type="b200" color="secondary">
            {jobTypes[index] ?? ''}
          </Text>
          <Text color="secondary">{shiftCodes[index] ?? ''}</Text>
        </TableCell>
        {Array(visibleHeaderCount - 3).fill(<TableCell />)}
      </TableRow>
    ));

  const renderCaseIdCell = (placementId: number, caseId: string, placementType: string | null) => {
    return (
      <>
        <Text type="b300">
          <Link type="secondary" href={`${PLACEMENT_DETAILS_BASEURL}/${placementId}`}>
            {caseId ?? '[Case ID Missing]'}
          </Link>
        </Text>
        <Text type="b200" color="secondary">
          {placementType ?? ''}
        </Text>
      </>
    );
  };

  const renderSortedTableData = () =>
    paginatedData.map(
      ({
        alias,
        allocatedJobShifts,
        associateFullName,
        caseId,
        closedAtDate,
        daysInCategory,
        id,
        jobs,
        jobTypes,
        originalShiftCode,
        placementStartDate,
        placementEndDate,
        placementType,
        shiftCodes,
        status,
        totalAccommodatedDaysByAssociate,
        jobsDisplayed,
        jobSuffix,
        previousSite,
        transferredAtDate,
      }) => {
        const associateNameOrAlias = handleEmployeeCell(associateFullName, alias, originalShiftCode);
        const caseIdCell = renderCaseIdCell(id, caseId, placementType);

        return currentTab !== 'transferredInPlacements' ? (
          <UncontrolledExpandableRow key={id}>
            <TableCell>{associateNameOrAlias}</TableCell>
            <TableCell>{caseIdCell}</TableCell>
            <TableCell>{jobsDisplayed && handleJobs(jobs, jobTypes, shiftCodes, jobSuffix)}</TableCell>
            <TableCell>{placementStartDate}</TableCell>
            <TableCell>{placementEndDate}</TableCell>
            <TableCell>{daysInCategory}</TableCell>
            {canShow180DayCounter && <TableCell>{totalAccommodatedDaysByAssociate}</TableCell>}
            {currentTab === 'closedPlacements' && <TableCell>{closedAtDate}</TableCell>}
            <TableCell>
              <Tag type={currentTab === 'activePlacements' ? 'success' : 'neutral'}>{status}</Tag>
            </TableCell>
            {jobsDisplayed && handleAllocatedJobShifts(allocatedJobShifts, jobs, jobTypes, shiftCodes, jobSuffix)}
          </UncontrolledExpandableRow>
        ) : (
          <UncontrolledExpandableRow key={id}>
            <TableCell>{associateNameOrAlias}</TableCell>
            <TableCell>{renderCaseIdCell(id, caseId, placementType)}</TableCell>
            <TableCell>{previousSite}</TableCell>
            <TableCell>{transferredAtDate}</TableCell>
            <TableCell>{daysInCategory}</TableCell>
            <TableCell>
              <Tag type="neutral">{status}</Tag>
            </TableCell>
          </UncontrolledExpandableRow>
        );
      },
    );

  return (
    <Table
      headerRows={1}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={onSort}
      showDividers
      showExpandedDividers
      fixHeaderRows
      rowComponents={[UncontrolledExpandableRow, TableRow]}
      nestedRows
    >
      <TableRow>
        {tableHeaders
          .filter((header) => header.condition !== false)
          .map((header) => (
            <TableCell key={header.sortColumn} sortColumn={header.sortColumn} id={header.id}>
              {header.label}
            </TableCell>
          ))}
      </TableRow>
      {renderSortedTableData()}
    </Table>
  );
};

export default PlacementsOverviewTable;
